package io.horion.cv.viewModel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.cv.api.datasource.job.ApplyJob
import io.horion.cv.api.datasource.job.ApplyJobStatus
import io.horion.cv.api.usecase.JobUseCase
import io.horion.cv.models.ExperienceLevel
import io.horion.cv.models.Job
import io.horion.cv.models.JobStatus
import io.horion.cv.models.JobType
import io.horion.cv.models.SalaryRange
import io.horion.cv.uiState.JobUiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class JobViewModel(
    private val jobUseCase: JobUseCase,
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main)
) {

    var jobUiState by mutableStateOf(JobUiState())
        private set

    var editingJobId by mutableStateOf<String?>(null)
    var editingApplyId by mutableStateOf<String?>(null)
     var editingResponsability by mutableStateOf<String?>(null)
     var editingRequirement by mutableStateOf<String?>(null)
     var editingTag by mutableStateOf<String?>(null)


    // ─── Validation ──────────────────────────────────────────────────────────

    var showValidationWarning by mutableStateOf(false)
        private set

    var missingFieldIds by mutableStateOf<List<String>>(emptyList())
        private set

    fun validateRequiredFields(): Boolean {
        val job = jobUiState.currentJob
        val missing = mutableListOf<String>()

        if (job.title.isBlank()) missing.add("title")
        if (job.enterprise.isBlank()) missing.add("enterprise")
        if (job.city.isBlank()) missing.add("city")
        if (job.description.isBlank()) missing.add("description")
        if (job.contactEmail.isBlank()) missing.add("contactEmail")
        if (job.expire.isBlank()) missing.add("expire")

        missingFieldIds = missing
        showValidationWarning = missing.isNotEmpty()
        return missing.isEmpty()
    }

    fun dismissValidationWarning() {
        showValidationWarning = false
    }

    // ─── CRUD Job ─────────────────────────────────────────────────────────────

    fun createJob() {
        if (!validateRequiredFields()) return
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null, success = null)
            try {
                val response = jobUseCase.createCv(jobUiState.currentJob.toApi())
                when {
                    response == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    response.success -> {
                        jobUiState = jobUiState.copy(loading = false, success = response.message)
                        resetCurrentJob()
                    }
                    else -> jobUiState = jobUiState.copy(loading = false, error = response.message)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateJob(uuid: String) {
        if (!validateRequiredFields()) return
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null, success = null)
            try {
                val response = jobUseCase.updateJob(uuid, jobUiState.currentJob.toApi())
                when {
                    response == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    response.success -> jobUiState = jobUiState.copy(loading = false, success = response.message)
                    else -> jobUiState = jobUiState.copy(loading = false, error = response.message)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun deleteJob(uuid: String) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null, success = null)
            try {
                val response = jobUseCase.deleteJob(uuid)
                when {
                    response == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    response.success -> jobUiState = jobUiState.copy(
                        loading = false,
                        success = response.message,
                        jobs = jobUiState.jobs.filter { it.id != uuid }
                    )
                    else -> jobUiState = jobUiState.copy(loading = false, error = response.message)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    // ─── Candidatures ─────────────────────────────────────────────────────────

    fun applyToJob(applyJob: ApplyJob) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null, success = null)
            try {
                val response = jobUseCase.applyJob(applyJob)
                when {
                    response == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    response.success -> jobUiState = jobUiState.copy(loading = false, success = response.message)
                    else -> jobUiState = jobUiState.copy(loading = false, error = response.message)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun updateApplyStatus(uuid: String, status: ApplyJobStatus, comments: String? = null) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null, success = null)
            try {
                val response = jobUseCase.updateApplyStatus(uuid, status, comments)
                when {
                    response == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    response.success -> {
                        val updatedApplies = jobUiState.applies.map { apply ->
                            if (apply.uuid == uuid) apply.copy(status = status) else apply
                        }
                        jobUiState = jobUiState.copy(
                            loading = false,
                            success = response.message,
                            applies = updatedApplies
                        )
                    }
                    else -> jobUiState = jobUiState.copy(loading = false, error = response.message)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadAppliesForJob(jobUuid: String) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null)
            try {
                val applies = jobUseCase.getAppliesJob(jobUuid)
                when {
                    applies == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    else -> jobUiState = jobUiState.copy(loading = false, applies = applies)
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    // ─── Chargement de listes ─────────────────────────────────────────────────

    fun loadAllJobs() {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null)
            try {
                val apiJobs = jobUseCase.findAllJobs()
                val localJobs = apiJobs.map { it.toLocal() }
                jobUiState = jobUiState.copy(loading = false, jobs = localJobs)
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadJobsByEnterprise(enterpriseUuid: String) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null)
            try {
                val jobs = jobUseCase.findJobsByEnterpriseUuid(enterpriseUuid)
                when {
                    jobs == null -> jobUiState = jobUiState.copy(isConnected = false, loading = false)
                    else -> jobUiState = jobUiState.copy(loading = false, jobs = jobs.map { it.toLocal() })
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadJobByUuid(uuid: String) {
        scope.launch {
            jobUiState = jobUiState.copy(loading = true, error = null)
            try {
                val job = jobUseCase.findJobByUuid(uuid)
                if (job != null) {
                    jobUiState = jobUiState.copy(loading = false, selectedJob = job.toLocal())
                } else {
                    jobUiState = jobUiState.copy(loading = false, error = "Offre introuvable")
                }
            } catch (e: Exception) {
                jobUiState = jobUiState.copy(loading = false, error = e.message)
            }
        }
    }

    // ─── Mise à jour des champs ───────────────────────────────────────────────

    fun updateTitle(title: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(title = title))
        revalidateIfNeeded()
    }

    fun updateEnterprise(enterprise: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(enterprise = enterprise))
        revalidateIfNeeded()
    }

    fun updateCity(city: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(city = city))
        revalidateIfNeeded()
    }

    fun updateDescription(description: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(description = description))
        revalidateIfNeeded()
    }

    fun updateContactEmail(email: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(contactEmail = email))
        revalidateIfNeeded()
    }

    fun updateExpireDate(expire: String) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(expire = expire))
        revalidateIfNeeded()
    }

    fun updateJobType(type: JobType) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(type = type))
    }

    fun updateExperienceLevel(level: ExperienceLevel) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(experience = level))
    }

    fun updateIsRemote(isRemote: Boolean) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(isRemote = isRemote))
    }

    fun updateJobStatus(status: JobStatus) {
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(status = status))
    }

    fun updateSalaryRange(min: Int, max: Int, currency: String = "CFA") {
        jobUiState = jobUiState.copy(
            currentJob = jobUiState.currentJob.copy(
                salaryRange = SalaryRange(min = min, max = max, currency = currency)
            )
        )
    }

    // ─── Listes imbriquées ────────────────────────────────────────────────────

    fun updateRequirement(index: Int, value: String) {
        val list = jobUiState.currentJob.requirements.toMutableList()
        if (index in list.indices) list[index] = value else list.add(value)
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(requirements = list))
    }

    fun deleteRequirement(index: Int) {
        val list = jobUiState.currentJob.requirements.toMutableList()
        if (index in list.indices) {
            list.removeAt(index)
            jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(requirements = list))
        }
    }

    fun updateResponsibility(index: Int, value: String) {
        val list = jobUiState.currentJob.responsibilities.toMutableList()
        if (index in list.indices) list[index] = value else list.add(value)
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(responsibilities = list))
    }

    fun deleteResponsibility(index: Int) {
        val list = jobUiState.currentJob.responsibilities.toMutableList()
        if (index in list.indices) {
            list.removeAt(index)
            jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(responsibilities = list))
        }
    }

    fun updateTag(index: Int, value: String) {
        val list = jobUiState.currentJob.tags.toMutableList()
        if (index in list.indices) list[index] = value else list.add(value)
        jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(tags = list))
    }

    fun deleteTag(index: Int) {
        val list = jobUiState.currentJob.tags.toMutableList()
        if (index in list.indices) {
            list.removeAt(index)
            jobUiState = jobUiState.copy(currentJob = jobUiState.currentJob.copy(tags = list))
        }
    }

    // ─── Filtrage local ───────────────────────────────────────────────────────

    fun filterByType(type: JobType?) {
        jobUiState = jobUiState.copy(filterType = type)
    }

    fun filterByExperience(level: ExperienceLevel?) {
        jobUiState = jobUiState.copy(filterExperienceLevel = level)
    }

    fun filterRemoteOnly(remote: Boolean) {
        jobUiState = jobUiState.copy(filterRemoteOnly = remote)
    }

    fun updateSearchQuery(query: String) {
        jobUiState = jobUiState.copy(searchQuery = query)
    }

    fun updateSearchQueryLocation(query: String) {
        jobUiState = jobUiState.copy(searchQueryLocation = query)
    }

    fun filteredJobs(): List<Job> {
        val state = jobUiState
        return state.jobs.filter { job ->


            val matchesType = state.filterType == null || job.type == state.filterType
            val matchesLevel = state.filterExperienceLevel == null || job.experience == state.filterExperienceLevel
            val matchesRemote = !state.filterRemoteOnly || job.isRemote

            val generalQuery = state.searchQuery.trim().lowercase()
            val matchesGeneral = generalQuery.isEmpty() ||
                    job.title.lowercase().contains(generalQuery) ||
                    job.enterprise.lowercase().contains(generalQuery) ||
                    job.tags.any { it.lowercase().contains(generalQuery) }

            val locationQuery = state.searchQueryLocation.trim().lowercase()
            val matchesLocation = locationQuery.isEmpty() ||
                    job.city.lowercase().contains(locationQuery)

            matchesType && matchesLevel && matchesRemote && matchesGeneral && matchesLocation
        }
    }


    fun selectJob(job: Job) {
        jobUiState = jobUiState.copy(selectedJob = job)
    }

    fun clearSelectedJob() {
        jobUiState = jobUiState.copy(selectedJob = null)
    }

    fun closeJobDetails() {
        jobUiState = jobUiState.copy(selectedJob = null)
    }

    fun loadJobIntoEditor(job: Job) {
        jobUiState = jobUiState.copy(currentJob = job)
        editingJobId = job.id
    }

    fun resetCurrentJob() {
        jobUiState = jobUiState.copy(currentJob = Job())
        editingJobId = null
    }

    fun clearError() {
        jobUiState = jobUiState.copy(error = null)
    }

    fun clearSuccess() {
        jobUiState = jobUiState.copy(success = null)
    }

    // ─── Helpers privés ───────────────────────────────────────────────────────

    private fun revalidateIfNeeded() {
        if (showValidationWarning) {
            validateRequiredFields()
            if (missingFieldIds.isEmpty()) showValidationWarning = false
        }
    }

// ─── Mappers local ↔ API ──────────────────────────────────────────────────────

private fun Job.toApi(): io.horion.cv.api.datasource.job.Job {
    return io.horion.cv.api.datasource.job.Job(
        id = this.id,
        uuid = this.id,
        enterprise = this.enterprise,
        title = this.title,
        city = this.city,
        type = io.horion.cv.api.datasource.job.JobType.valueOf(this.type.name),
        description = this.description,
        requirements = this.requirements,
        responsibilities = this.responsibilities,
        salaryRange = io.horion.cv.api.datasource.job.SalaryRange(
            min = this.salaryRange.min,
            max = this.salaryRange.max,
            currency = this.salaryRange.currency
        ),
        experience = io.horion.cv.api.datasource.job.ExperienceLevel.valueOf(this.experience.name),
        publicateAt = this.publicateAt,
        expire = this.expire,
        contactEmail = this.contactEmail,
        tags = this.tags,
        isRemote = this.isRemote,
        status = io.horion.cv.api.datasource.job.JobStatus.valueOf(this.status.name),
        createdAt = this.createdAt,
        updatedAt = this.updatedAt
    )
}

private fun io.horion.cv.api.datasource.job.Job.toLocal(): Job {
    return Job(
        id = this.uuid.ifEmpty { this.id },
        enterprise = this.enterprise ?: "",
        title = this.title,
        city = this.city,
        type = runCatching { JobType.valueOf(this.type.name) }.getOrDefault(JobType.FREELANCE),
        description = this.description,
        requirements = this.requirements,
        responsibilities = this.responsibilities,
        salaryRange = this.salaryRange?.let {
            SalaryRange(min = it.min, max = it.max, currency = it.currency)
        } ?: SalaryRange(min = 0, max = 0, currency = ""),
        experience = runCatching { ExperienceLevel.valueOf(this.experience.name) }.getOrDefault(ExperienceLevel.MID),
        publicateAt = this.publicateAt,
        expire = this.expire ?: "",
        contactEmail = this.contactEmail,
        tags = this.tags,
        isRemote = this.isRemote,
        status = runCatching { JobStatus.valueOf(this.status.name) }.getOrDefault(JobStatus.ACTIVE),
        createdAt = this.createdAt,
        updatedAt = this.updatedAt ?: ""
    )
}
}